import {useState, useEffect, useCallback} from 'react';
import {DocumentReportService} from '../services/documentReportService';
import {Provider, useSelector} from 'react-redux';
import {RootState, store} from '../store/store';
import DocumentTemplateRenderer from '../components/documentTemplate/DocumentTemplateRenderer';
import {createRoot} from 'react-dom/client';
import {DocumentTemplate} from '../types/documentTemplate';
import {UserHrDocumentService} from '../services/userHrDocumentService';
import {UserTrainingService} from '../services/userTrainingService';
import {UserApplicationService} from '../services/userApplicationService';
import {UserEvaluationService} from '../services/userEvaluationServices';
import {UserJobRoleService} from '../services/userJobRoleService';
import {UserBackgroundDocumentService} from '../services/userBackgroundDocumentService';
import {UserMedicalDocumentService} from '../services/userMedicalDocumentService';
import {UserCredentialService} from '../services/userCredentialService';

interface ModalConfig {
  title: string;
  customMessages: {
    preparing: string;
    processing: string;
    downloading: string;
    complete: string;
  };
  waitMessage: string;
  buttonText: string;
}

interface SelectedRecord {
  id: string;
  documentType: string;
}

const recordService = (record: any) => {
  switch (record.documentType) {
    case 'Document':
      return UserHrDocumentService;
    case 'Training':
      return UserTrainingService;
    case 'Credential':
      return UserCredentialService;
    case 'Application':
      return UserApplicationService;
    case 'Evaluation':
      return UserEvaluationService;
    case 'Role':
      return UserJobRoleService;
    case 'BackgroundDocument':
      return UserBackgroundDocumentService;
    case 'MedicalDocument':
      return UserMedicalDocumentService;
    default:
      return null;
  }
};

type DownloadStatus = 'preparing' | 'processing' | 'downloading' | 'complete';

export const useProcessDocuments = (
  selectedRecords: any[],
  openViewerModal: (config: any) => void,
) => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  const [downloadStatus, setDownloadStatus] =
    useState<DownloadStatus>('preparing');
  const company = useSelector((state: RootState) => state.company.company);

  const documentTypeMap: {[key: string]: string} = {
    Document: 'UserHrDocument',
    Training: 'UserTraining',
    Application: 'UserApplication',
    Evaluation: 'UserEvaluation',
    Role: 'UserJobRole',
  };

  const getTemplateHtml = useCallback(
    async (record: SelectedRecord): Promise<string> =>
      new Promise(resolve => {
        const service = recordService(record); // tu factory/service detector
        if (service) {
          service.show(record.id).then((response: any) => {
            if (response.documentTemplateSolution?.snapshot) {
              const template = new DocumentTemplate(
                response.documentTemplateSolution.snapshot,
              );
              const tempDiv = document.createElement('div');
              document.body.appendChild(tempDiv);

              const root = createRoot(tempDiv);
              root.render(
                <Provider store={store}>
                  <DocumentTemplateRenderer
                    template={template}
                    mode="view"
                    companyInfo={{logoUrl: company.logoUrl, name: company.name}}
                  />
                </Provider>,
              );

              setTimeout(() => {
                const html = tempDiv.innerHTML;
                root.unmount();
                document.body.removeChild(tempDiv);
                resolve(html);
              }, 100);
            }
          });
        }
      }),
    [company],
  );

  const processDocuments = async (action: 'download' | 'print' | 'view') => {
    if (selectedRecords.length === 0) {
      alert(`Please select at least one document to ${action}.`);
      return;
    }
    try {
      const config = {
        download: {
          title: 'Document Download',
          customMessages: {
            preparing: 'Preparing documents for download...',
            processing: 'Processing and combining documents...',
            downloading: 'Starting download...',
            complete: 'Download complete!',
          },
          waitMessage:
            'Please keep this page open until the download is complete',
          buttonText: 'Close',
        },
        view: {
          title: 'Document Preview',
          customMessages: {
            preparing: 'Preparing documents for viewing...',
            processing: 'Processing and combining documents...',
            downloading: 'Preparing document viewer...',
            complete: 'Documents ready to view!',
          },
          waitMessage:
            'Please keep this page open while we prepare your documents',
          buttonText: 'Close',
        },
        print: {
          title: 'Document Print',
          customMessages: {
            preparing: 'Preparing documents for printing...',
            processing: 'Processing and combining documents...',
            downloading: 'Preparing print layout...',
            complete: 'Documents ready to print!',
          },
          waitMessage:
            'Please keep this page open while we prepare your documents for printing',
          buttonText: 'Close',
        },
      };

      setModalConfig(config[action]);
      setIsDownloadModalOpen(true);
      setDownloadStatus('preparing');

      const documentsToDownload = await Promise.all(
        selectedRecords.map(async record => {
          const document: any = {
            id: record.id,
            documentType:
              documentTypeMap[record.documentType] || record.documentType,
          };

          if (
            record.documentType !== 'Document' &&
            record.documentType !== 'Training'
          ) {
            document.htmlRender = await getTemplateHtml(record);
          }

          return document;
        }),
      );

      setDownloadStatus('processing');

      const response =
        await DocumentReportService.batchDownload(documentsToDownload);

      if (!response.data?.url) {
        throw new Error('No valid download URL received');
      }

      setDownloadStatus('downloading');

      if (action === 'download') {
        const link = document.createElement('a');
        link.href = response.data.url;
        link.download = `combined_documents_${new Date().toISOString().split('T')[0]}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (action === 'view') {
        openViewerModal({
          id: 'batch-view',
          name: `Selected documents (${selectedRecords.length})`,
          completedAt: new Date().toISOString(),
          attachment: {url: response.data.url},
          docType: 'file',
          recordType: 'BatchDocument',
        });
      } else {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          throw new Error('Failed to open the print window');
        }
        printWindow.document.write(`
          <html>
            <head>
              <title>Imprint Document</title>
              <style>
                @page {
                  size: A4;
                  margin: 0;
                }
                body, html {
                  margin: 0;
                  padding: 0;
                  width: 210mm;
                  height: 297mm;
                  overflow: hidden;
                }
                iframe {
                  position: fixed;
                  top: 0;
                  left: 0;
                  width: 210mm;
                  height: 297mm;
                  border: none;
                  box-sizing: border-box;
                }
                @media print {
                  @page {
                    size: A4;
                    margin: 0;
                  }
                  body {
                    margin: 0;
                    padding: 0;
                    width: 210mm;
                    height: 297mm;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                  }
                  iframe {
                    position: absolute;
                    width: 210mm;
                    height: 297mm;
                    object-fit: contain;
                  }
                }
              </style>
              <script>
                function checkPDFLoaded(iframe) {
                  try {
                    if (iframe.contentWindow.document.readyState === 'complete' &&
                        iframe.contentWindow.document.body &&
                        iframe.contentWindow.document.body.innerHTML !== '') {
                      setTimeout(() => {
                        window.print();
                        setTimeout(() => window.close(), 1000);
                      }, 500);
                    } else {
                      setTimeout(() => checkPDFLoaded(iframe), 100);
                    }
                  } catch (e) {
                    setTimeout(() => {
                      window.print();
                      setTimeout(() => window.close(), 1000);
                    }, 2000);
                  }
                }
              </script>
            </head>
            <body>
              <iframe
                src="${response.data.url}#toolbar=0&navpanes=0&view=Fit"
                onload="checkPDFLoaded(this)"
              ></iframe>
            </body>
          </html>
        `);
        printWindow.document.close();
      }

      setDownloadStatus('complete');

      setTimeout(() => {
        setIsDownloadModalOpen(false);
        setDownloadStatus('preparing');
      }, 2000);
    } catch (error) {
      if (error instanceof Error) {
        alert(
          `An error occurred while ${action}ing the documents. Please try again.`,
        );
      }
      setIsDownloadModalOpen(false);
      setDownloadStatus('preparing');
    }
  };

  useEffect(() => {
    return () => {
      setIsDownloadModalOpen(false);
      setDownloadStatus('preparing');
    };
  }, []);

  return {
    isDownloadModalOpen,
    downloadStatus,
    setDownloadStatus,
    modalConfig,
    setModalConfig,
    processDocuments,
    setIsDownloadModalOpen,
  };
};
